import type { ColorMode } from '@/types/color-mode.type'
import Fade from '@mui/material/Fade'
import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-data-grid-pro/themeAugmentation'
import { createElement, type CSSProperties } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import type { IconType } from 'react-icons'
import { LuArrowDown, LuArrowLeftToLine, LuArrowRightToLine, LuArrowUp, LuColumns3, LuEllipsisVertical, LuEyeOff, LuFilter, LuX } from 'react-icons/lu'
import { TbInfoCircleFilled } from 'react-icons/tb'

/** MUI Select uses styled(`svg`) + `as={IconComponent}`; strip non-DOM props react-icons would forward. */
function SelectChevronDownIcon({ ownerState, className, style }: { ownerState?: { size?: string }; className?: string; style?: CSSProperties }) {
    const size = ownerState?.size === 'x-small' ? 16 : 18
    return createElement(FiChevronDown, { size, className, style })
}

/** Data grid icon slots get MUI SvgIcon props (`fontSize="small"`); keep only the DOM-safe ones and pin the size. */
function gridIcon(Icon: IconType, size = 18) {
    function GridIcon({ className, style }: { className?: string; style?: CSSProperties }) {
        return createElement(Icon, { size, className, style, 'aria-hidden': true })
    }
    GridIcon.displayName = `GridIcon(${Icon.name})`
    return GridIcon
}

// Column header ⋮ trigger and column menu items, shared by every grid; a grid's own `slots` merge over these per key.
const DATA_GRID_ICON_SLOTS = {
    // Header: sort arrow (the default unsorted hover arrow reuses these two) and the active-filter badge.
    columnSortedAscendingIcon: gridIcon(LuArrowUp),
    columnSortedDescendingIcon: gridIcon(LuArrowDown),
    // The funnel fills its box more than the arrows do, so it runs a size down to read at the same weight.
    columnFilteredIcon: gridIcon(LuFilter, 15),
    columnMenuIcon: gridIcon(LuEllipsisVertical),
    columnMenuSortAscendingIcon: gridIcon(LuArrowUp),
    columnMenuSortDescendingIcon: gridIcon(LuArrowDown),
    columnMenuClearIcon: gridIcon(LuX),
    columnMenuPinLeftIcon: gridIcon(LuArrowLeftToLine),
    columnMenuPinRightIcon: gridIcon(LuArrowRightToLine),
    columnMenuFilterIcon: gridIcon(LuFilter),
    columnMenuHideIcon: gridIcon(LuEyeOff),
    columnMenuManageColumnsIcon: gridIcon(LuColumns3),
}

const ALERT_ICON_PROPS = { size: 20, 'aria-hidden': true as const }

function createAlertIcon() {
    return createElement(TbInfoCircleFilled, ALERT_ICON_PROPS)
}

function alertStandardStyle(main: string, fadeBase: string, isDark: boolean) {
    return {
        backgroundColor: `color-mix(in srgb, ${main} 10%, ${fadeBase})`,
        backgroundImage: 'none',
        color: isDark ? `color-mix(in srgb, ${main} 55%, white)` : `color-mix(in srgb, ${main} 72%, black)`,
        border: `1px solid color-mix(in srgb, ${main} 28%, ${fadeBase})`,
        boxShadow: 'none',
        '& .MuiAlert-icon': {
            color: main,
            opacity: 1,
        },
        '& .MuiAlert-action': {
            color: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)',
        },
    }
}

function alertFilledStyle(main: string, contrastText = '#ffffff') {
    return {
        backgroundColor: main,
        color: contrastText,
        border: '1px solid transparent',
        boxShadow: 'none',
        '& .MuiAlert-icon': {
            color: contrastText,
            opacity: 1,
        },
        '& .MuiAlert-action': {
            color: contrastText,
            opacity: 0.8,
        },
    }
}

interface ChipSoftStyleOptions {
    /** Palette main used for hover deepening and dark-mode tint derivation. */
    base: string
    /** Same as `base` when unset; lets dark derive from a brighter variant. */
    darkBase?: string
    lightBg: string
    lightText: string
    borderColor?: string
}

/** Soft chip: tinted fill + matching dark text; dark mode derives the tint from `base`. */
function chipSoftStyle({ base, darkBase, lightBg, lightText, borderColor }: ChipSoftStyleOptions, isDark: boolean, fadeBase: string) {
    const tintBase = darkBase ?? base
    const backgroundColor = isDark ? `color-mix(in srgb, ${tintBase} 16%, ${fadeBase})` : lightBg
    const hoverBackgroundColor = isDark ? `color-mix(in srgb, ${tintBase} 24%, ${fadeBase})` : `color-mix(in srgb, ${lightBg} 85%, ${base})`
    return {
        backgroundColor,
        color: isDark ? `color-mix(in srgb, ${tintBase} 60%, white)` : lightText,
        border: `1px solid ${borderColor ?? 'transparent'}`,
        '& .MuiChip-icon': {
            color: 'inherit',
            opacity: 0.8,
        },
        '& .MuiChip-deleteIcon': {
            color: 'inherit',
            opacity: 0.75,
            '&:hover': {
                color: 'inherit',
                opacity: 1,
            },
        },
        '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': {
            backgroundColor: hoverBackgroundColor,
        },
    }
}

/** Thin, low-contrast scrollbar for Menu / Select / Autocomplete surfaces. */
const sleekScrollbarStyles = {
    scrollbarWidth: 'thin',
    scrollbarColor: 'color-mix(in srgb, var(--text-primary) 28%, transparent) transparent',
    '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
        marginBlock: 4,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'color-mix(in srgb, var(--text-primary) 28%, transparent)',
        borderRadius: 999,
        border: '1.5px solid transparent',
        backgroundClip: 'content-box',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'color-mix(in srgb, var(--text-primary) 45%, transparent)',
    },
} as const

export function createAppTheme(mode: ColorMode, fontFamily: string) {
    const isDark = mode === 'dark'
    const tintFadeBase = isDark ? '#1e1e24' : '#ffffff'
    const alertColors = {
        success: '#26a475',
        // Brighter blue reads better as filled toast than palette info (#0115d2)
        info: isDark ? '#7b93f5' : '#476be1',
        warning: '#f79009',
        error: '#f04438',
        neutral: isDark ? '#a1a1aa' : '#344054',
    } as const

    return createTheme({
        typography: {
            fontFamily,
            h1: {
                fontSize: '32px',
            },
            h2: {
                fontSize: '24px',
            },
            h3: {
                fontSize: '18px',
            },
            h4: {
                fontSize: '16px',
            },
            body1: {
                fontSize: '14px',
            },
            body2: {
                fontSize: '12px',
            },
        },
        palette: {
            mode,
            primary: {
                // Keep in sync with --primary in globals.css (:root / html.dark)
                main: mode === 'dark' ? '#7b93f5' : '#476be1',
                dark: mode === 'dark' ? '#93a8f8' : '#365dbf',
                light: mode === 'dark' ? '#a3b6fa' : '#6b8aff',
                contrastText: '#ffffff',
            },
            secondary: {
                main: '#f44336',
            },
            // Keep in sync with --color-error/success/warning/info in globals.css (@theme static)
            error: {
                main: '#f04438',
            },
            success: {
                main: '#26a475',
            },
            warning: {
                main: '#f79009',
            },
            info: {
                main: '#0115d2',
            },
            brand: {
                // Keep in sync with --color-brand / --btn-brand-* in globals.css
                main: '#5e4cef', // --color-brand, --color-brand-600
                dark: '#4530d2', // --color-brand-700, --btn-brand-bg-hover
                light: '#a0abff', // --color-brand-300
                contrastText: '#ffffff', // --color-brand-foreground
            },
            'sanya-primary': {
                // MUI palette requires parseable color literals; keep in sync with globals.css
                main: '#ffa050', // --color-sanya-primary, --btn-sanya-primary-bg
                dark: '#e08d46', // --btn-sanya-primary-bg-hover
                light: '#ffcb9f', // --btn-sanya-primary-bg-disabled
                contrastText: '#000000',
            },
            // Cancel / secondary-action style — keep in sync with --btn-outline-* in globals.css
            neutral: {
                main: mode === 'dark' ? '#a1a1aa' : '#344054',
                dark: mode === 'dark' ? '#d4d4d8' : '#1d2939',
                light: mode === 'dark' ? '#71717a' : '#667085',
                contrastText: mode === 'dark' ? '#16161a' : '#ffffff',
            },
            // Solid foreground CTA (auth/onboarding primary) — near-black in light mode, inverted in dark.
            dark: {
                main: mode === 'dark' ? '#f4f4f5' : '#0a0a0a',
                dark: mode === 'dark' ? '#ffffff' : '#262626',
                light: mode === 'dark' ? '#ffffff' : '#3f3f46',
                contrastText: mode === 'dark' ? '#1c1c21' : '#ffffff',
            },
            // Page-surface bordered CTA — pairs with the 'dark' color as the secondary action.
            light: {
                main: mode === 'dark' ? '#1c1c21' : '#ffffff',
                dark: mode === 'dark' ? '#22222a' : '#f6f6f6',
                light: mode === 'dark' ? '#27272a' : '#ffffff',
                contrastText: mode === 'dark' ? '#f4f4f5' : '#101828',
            },
            // Keep primary/secondary in sync with --foreground / --foreground-muted in globals.css (:root / html.dark),
            // so Typography color="text.*" matches text-foreground / text-foreground-muted.
            // Keep disabled input text / icons / borders in sync with --text-disabled
            // (MUI defaults split text.disabled vs action.disabled and look mismatched).
            text: {
                primary: mode === 'dark' ? '#f4f4f5' : '#101828',
                secondary: mode === 'dark' ? '#a1a1aa' : '#475467',
                disabled: mode === 'dark' ? '#71717a' : '#a0a0a0',
            },
            action: {
                disabled: mode === 'dark' ? '#71717a' : '#a0a0a0',
                disabledBackground: mode === 'dark' ? '#27272a' : '#f5f5f5',
            },
            // Keep in sync with --border in globals.css (:root / html.dark)
            divider: mode === 'dark' ? '#3f3f46' : '#e6e6e6',
            ...(mode === 'dark'
                ? {
                      background: {
                          default: '#16161a',
                          paper: '#1e1e24',
                      },
                  }
                : {}),
        },
        components: {
            // Every data grid: no scroll chaining or rubber-band bounce, so scrolling past a grid's edge doesn't
            // move the page or drawer behind it.
            MuiDataGrid: {
                defaultProps: {
                    slots: DATA_GRID_ICON_SLOTS,
                },
                styleOverrides: {
                    virtualScroller: { overscrollBehavior: 'none' },
                    scrollbar: { overscrollBehavior: 'none' },
                },
            },
            MuiSelect: {
                defaultProps: {
                    IconComponent: SelectChevronDownIcon,
                    MenuProps: {
                        PaperProps: {
                            elevation: 0,
                        },
                    },
                },
                styleOverrides: {
                    root: {
                        variants: [
                            {
                                props: { size: 'x-small' },
                                style: {
                                    fontSize: '12px',
                                    '& .MuiSelect-select': {
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                        paddingRight: 28,
                                    },
                                    '& .MuiSelect-icon': {
                                        fontSize: 16,
                                        width: 16,
                                        height: 16,
                                        right: 7,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                    },
                                    '& .MuiSelect-iconOpen': {
                                        transform: 'translateY(-50%) rotate(180deg)',
                                    },
                                },
                            },
                        ],
                    },
                    icon: {
                        '&.Mui-disabled': {
                            color: 'var(--text-disabled)',
                        },
                    },
                },
            },
            MuiNativeSelect: {
                defaultProps: {
                    IconComponent: SelectChevronDownIcon,
                },
                styleOverrides: {
                    root: {
                        variants: [
                            {
                                props: { size: 'x-small' },
                                style: {
                                    fontSize: '12px',
                                    '& .MuiNativeSelect-select': {
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                        paddingTop: 4,
                                        paddingBottom: 4,
                                        paddingRight: 28,
                                    },
                                    '& .MuiNativeSelect-icon': {
                                        fontSize: 16,
                                        width: 16,
                                        height: 16,
                                        right: 7,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                    },
                                    '& .MuiNativeSelect-iconOpen': {
                                        transform: 'translateY(-50%) rotate(180deg)',
                                    },
                                },
                            },
                        ],
                    },
                    icon: {
                        '&.Mui-disabled': {
                            color: 'var(--text-disabled)',
                        },
                    },
                },
            },
            MuiAutocomplete: {
                defaultProps: {
                    popupIcon: createElement(FiChevronDown, { size: 18 }),
                },
                styleOverrides: {
                    listbox: {
                        padding: '4px 0',
                        ...sleekScrollbarStyles,
                    },
                    option: {
                        borderRadius: '6px',
                        margin: '2px 6px',
                        padding: '8px 10px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        '&[aria-selected="true"]': {
                            backgroundColor: 'var(--brand-soft)',
                        },
                        '&.Mui-focused': {
                            backgroundColor: 'var(--hover)',
                        },
                        '&[aria-selected="true"].Mui-focused': {
                            backgroundColor: 'var(--brand-soft)',
                        },
                    },
                },
            },
            MuiMenu: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    paper: {
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
                        backgroundImage: 'none',
                        marginTop: '4px',
                        ...sleekScrollbarStyles,
                        // `<Menu arrow>` from '@/components/ui': a tooltip-style arrow pointing up at the anchor. The
                        // component sets data-arrow and the arrow's offset (--menu-arrow-left / --menu-arrow-right).
                        '&[data-arrow]': {
                            marginTop: '10px',
                            // Room for the arrow, which sits half outside the paper.
                            overflow: 'visible',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: -1,
                                left: 'var(--menu-arrow-left, auto)',
                                right: 'var(--menu-arrow-right, auto)',
                                width: 'var(--menu-arrow-size)',
                                height: 'var(--menu-arrow-size)',
                                backgroundColor: 'inherit',
                                // Bordered on the two sides that show, like the paper's own border.
                                borderTop: '1px solid var(--border)',
                                borderLeft: '1px solid var(--border)',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                    list: {
                        padding: '4px 0',
                        ...sleekScrollbarStyles,
                    },
                },
            },
            MuiMenuItem: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        margin: '2px 6px',
                        padding: '8px 10px',
                        minHeight: 40,
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: 'var(--text-primary)',
                        transition: 'background-color 120ms ease',
                        '&:hover': {
                            backgroundColor: 'var(--hover)',
                        },
                        '&.Mui-focusVisible': {
                            backgroundColor: 'var(--hover)',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'var(--brand-soft)',
                            '&:hover': {
                                backgroundColor: 'var(--brand-soft)',
                            },
                            '&.Mui-focusVisible': {
                                backgroundColor: 'var(--brand-soft)',
                            },
                        },
                    },
                },
            },
            // Default Grow uses non-uniform scale(x, x²), which warps the rotated
            // arrow diamond during enter and makes a seam "appear" as it settles.
            // Fade avoids that; the 1px margin nudge closes any remaining subpixel gap.
            MuiTooltip: {
                defaultProps: {
                    slots: {
                        transition: Fade,
                    },
                },
                styleOverrides: {
                    tooltip: {
                        backgroundColor: 'var(--tooltip)',
                        color: 'var(--tooltip-foreground)',
                    },
                    arrow: {
                        color: 'var(--tooltip)',
                    },
                    popper: {
                        '&[data-popper-placement*="right"] .MuiTooltip-arrow': {
                            marginLeft: 'calc(-0.71em + 1px)',
                        },
                        '&[data-popper-placement*="left"] .MuiTooltip-arrow': {
                            marginRight: 'calc(-0.71em + 1px)',
                        },
                        '&[data-popper-placement*="top"] .MuiTooltip-arrow': {
                            marginBottom: 'calc(-0.71em + 1px)',
                        },
                        '&[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
                            marginTop: 'calc(-0.71em + 1px)',
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: () => ({
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontWeight: 600,
                        variants: [
                            {
                                props: { variant: 'contained' },
                                style: {
                                    boxShadow: 'var(--btn-shadow)',
                                    '&:hover': {
                                        boxShadow: 'var(--btn-shadow)',
                                    },
                                    '&.Mui-disabled': {
                                        boxShadow: 'none',
                                    },
                                },
                            },
                            {
                                props: { variant: 'outlined' },
                                style: {
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none',
                                    },
                                },
                            },
                            {
                                props: { variant: 'text' },
                                style: {
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none',
                                    },
                                },
                            },
                            {
                                props: {
                                    color: 'primary',
                                    variant: 'contained',
                                },
                                style: {
                                    backgroundColor: 'var(--btn-primary-bg)',
                                },
                            },
                            {
                                props: {
                                    color: 'brand',
                                    variant: 'contained',
                                },
                                style: {
                                    backgroundColor: 'var(--btn-brand-bg)',
                                    color: 'var(--color-brand-foreground)',
                                    '&:hover': {
                                        backgroundColor: 'var(--btn-brand-bg-hover)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'var(--btn-brand-bg-disabled)',
                                        color: 'rgba(255,255,255,0.7)',
                                        opacity: 0.7,
                                    },
                                },
                            },
                            {
                                props: {
                                    color: 'sanya-primary',
                                    variant: 'contained',
                                },
                                style: {
                                    textTransform: 'none',
                                    backgroundColor: 'var(--btn-sanya-primary-bg)', // --color-sanya-primary
                                    color: 'black',
                                    '&:hover': {
                                        backgroundColor: 'var(--btn-sanya-primary-bg-hover)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'var(--btn-sanya-primary-bg-disabled)',
                                        color: 'rgba(0,0,0,0.38)',
                                        opacity: 0.7,
                                    },
                                },
                            },
                            {
                                props: {
                                    color: 'neutral',
                                    variant: 'contained',
                                },
                                style: {
                                    backgroundColor: 'var(--bg-white)',
                                    color: 'var(--btn-outline-text)',
                                    border: '1px solid var(--btn-outline-border)',
                                    '&:hover': {
                                        backgroundColor: 'var(--hover)',
                                        border: '1px solid var(--btn-outline-border)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'var(--bg-disabled)',
                                        color: 'var(--text-disabled)',
                                        border: '1px solid var(--border-color)',
                                    },
                                },
                            },
                            {
                                // Solid neutral CTA — the primary auth/onboarding action.
                                props: { color: 'dark', variant: 'contained' },
                                style: {
                                    height: 48,
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    backgroundColor: isDark ? 'var(--foreground)' : '#0a0a0a',
                                    color: isDark ? 'var(--page)' : '#ffffff',
                                    '&:hover': isDark ? { opacity: 0.9 } : { backgroundColor: '#262626' },
                                    '&.Mui-disabled': {
                                        opacity: 0.6,
                                        backgroundColor: isDark ? 'var(--foreground)' : '#0a0a0a',
                                        color: isDark ? 'var(--page)' : '#ffffff',
                                    },
                                },
                            },
                            {
                                // Bordered secondary CTA — pairs with the 'dark' color.
                                props: { color: 'light', variant: 'contained' },
                                style: {
                                    height: 48,
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    backgroundColor: 'var(--page)',
                                    color: 'var(--foreground)',
                                    border: '2px solid var(--border)',
                                    '&:hover': {
                                        backgroundColor: 'var(--muted)',
                                        border: '2px solid var(--border)',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.6,
                                        color: 'var(--foreground)',
                                        border: '2px solid var(--border)',
                                    },
                                },
                            },
                            {
                                // Dense variant for table/cell actions — pairs with size="x-small" inputs.
                                props: { size: 'x-small' },
                                style: {
                                    fontSize: '12px',
                                    lineHeight: 1.4,
                                    padding: '3px 10px',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: -2,
                                        marginRight: 6,
                                        '& > *:nth-of-type(1)': {
                                            fontSize: 14,
                                        },
                                    },
                                    '& .MuiButton-endIcon': {
                                        marginLeft: 6,
                                        marginRight: -2,
                                        '& > *:nth-of-type(1)': {
                                            fontSize: 14,
                                        },
                                    },
                                },
                            },
                        ],
                    }),
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        variants: [
                            {
                                props: { variant: 'soft' },
                                style: chipSoftStyle(
                                    { base: '#667085', darkBase: '#a1a1aa', lightBg: '#fafafa', lightText: '#4f4d55', borderColor: 'var(--border)' },
                                    isDark,
                                    tintFadeBase
                                ),
                            },
                            {
                                props: { variant: 'soft', color: 'primary' },
                                style: chipSoftStyle(
                                    {
                                        base: '#476be1',
                                        darkBase: '#7b93f5',
                                        lightBg: 'color-mix(in srgb, #476be1 10%, #ffffff)',
                                        lightText: 'color-mix(in srgb, #476be1 70%, black)',
                                    },
                                    isDark,
                                    tintFadeBase
                                ),
                            },
                            {
                                props: { variant: 'soft', color: 'secondary' },
                                style: chipSoftStyle(
                                    {
                                        base: '#f44336',
                                        lightBg: 'color-mix(in srgb, #f44336 10%, #ffffff)',
                                        lightText: 'color-mix(in srgb, #f44336 65%, black)',
                                    },
                                    isDark,
                                    tintFadeBase
                                ),
                            },
                            {
                                props: { variant: 'soft', color: 'error' },
                                style: chipSoftStyle({ base: '#f04438', lightBg: '#ffe3e3', lightText: '#971717' }, isDark, tintFadeBase),
                            },
                            {
                                props: { variant: 'soft', color: 'warning' },
                                style: chipSoftStyle({ base: '#f79009', lightBg: '#fff1da', lightText: '#c47e0a' }, isDark, tintFadeBase),
                            },
                            {
                                props: { variant: 'soft', color: 'info' },
                                style: chipSoftStyle({ base: '#0115d2', darkBase: '#7b93f5', lightBg: '#ebf0fb', lightText: '#123c98' }, isDark, tintFadeBase),
                            },
                            {
                                props: { variant: 'soft', color: 'success' },
                                style: chipSoftStyle({ base: '#26a475', lightBg: '#e1faea', lightText: '#016626' }, isDark, tintFadeBase),
                            },
                            {
                                props: { variant: 'soft', color: 'neutral' },
                                style: chipSoftStyle({ base: '#4f4d55', darkBase: '#a1a1aa', lightBg: '#f1f1f1', lightText: '#4f4d55' }, isDark, tintFadeBase),
                            },
                            {
                                props: { variant: 'soft', color: 'brand' },
                                style: chipSoftStyle(
                                    {
                                        base: '#5e4cef',
                                        darkBase: '#a0abff',
                                        lightBg: 'color-mix(in srgb, #5e4cef 10%, #ffffff)',
                                        lightText: 'color-mix(in srgb, #5e4cef 68%, black)',
                                    },
                                    isDark,
                                    tintFadeBase
                                ),
                            },
                            {
                                props: { variant: 'soft', color: 'sanya-primary' },
                                style: chipSoftStyle(
                                    {
                                        base: '#ffa050',
                                        lightBg: 'color-mix(in srgb, #ffa050 16%, #ffffff)',
                                        lightText: 'color-mix(in srgb, #e06a10 65%, black)',
                                    },
                                    isDark,
                                    tintFadeBase
                                ),
                            },
                            {
                                props: { size: 'x-small' },
                                style: {
                                    height: 20,
                                    fontSize: '11px',
                                    '& .MuiChip-label': {
                                        paddingLeft: 6,
                                        paddingRight: 6,
                                    },
                                    '& .MuiChip-icon': {
                                        fontSize: 14,
                                        marginLeft: 4,
                                    },
                                    // Widen icon↔label gap only when an icon is present;
                                    // icon-less chips keep symmetric 6px label padding.
                                    '&:has(.MuiChip-icon) .MuiChip-label': {
                                        paddingLeft: 8,
                                    },
                                    '& .MuiChip-avatar': {
                                        fontSize: 11,
                                        width: 16,
                                        height: 16,
                                        marginLeft: 4,
                                        // MUI default marginRight: -6px fights the label gap below
                                        marginRight: 0,
                                    },
                                    '&:has(.MuiChip-avatar) .MuiChip-label': {
                                        paddingLeft: 8,
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: 14,
                                        marginRight: 4,
                                    },
                                },
                            },
                        ],
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        backgroundColor: 'var(--input)',
                        boxShadow: 'var(--input-shadow)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--border)',
                        },
                        '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--border-hover)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'var(--border-color-focus)',
                        },
                        '&.Mui-disabled': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--border)',
                            },
                            '& .MuiInputAdornment-root': {
                                color: 'var(--text-disabled)',
                                opacity: 1,
                            },
                            '& .MuiSelect-icon, & .MuiNativeSelect-icon, & .MuiAutocomplete-endAdornment': {
                                color: 'var(--text-disabled)',
                            },
                        },
                        '&:not(.MuiInputBase-multiline)': {
                            height: 40,
                        },
                        variants: [
                            {
                                props: { size: 'large' },
                                style: {
                                    '&:not(.MuiInputBase-multiline)': {
                                        height: 48,
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '12px 14px',
                                    },
                                },
                            },
                            {
                                props: { size: 'x-small' },
                                style: {
                                    fontSize: '12px',
                                    '&:not(.MuiInputBase-multiline)': {
                                        height: 32,
                                    },
                                    '&.MuiInputBase-multiline': {
                                        padding: '8.5px 10px',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        lineHeight: 1.4,
                                    },
                                    '&.MuiInputBase-multiline .MuiOutlinedInput-input': {
                                        padding: 0,
                                    },
                                },
                            },
                        ],
                    },
                    input: {
                        padding: '8.5px 14px',
                        '&.Mui-disabled': {
                            WebkitTextFillColor: 'var(--text-disabled)',
                            color: 'var(--text-disabled)',
                            opacity: 1,
                        },
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            color: 'var(--text-disabled)',
                            opacity: 1,
                        },
                    },
                },
            },
            // Inputs are forced to 40px (small); keep floating labels vertically centered.
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            color: 'var(--text-disabled)',
                        },
                        '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                            transform: 'translate(14px, 9px) scale(1)',
                        },
                        variants: [
                            {
                                props: { size: 'large' },
                                style: {
                                    '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                                        transform: 'translate(14px, 13px) scale(1)',
                                    },
                                },
                            },
                            {
                                props: { size: 'x-small' },
                                style: {
                                    fontSize: '12px',
                                    '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                                        transform: 'translate(10px, 6px) scale(1)',
                                    },
                                },
                            },
                        ],
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiAlert: {
                defaultProps: {
                    iconMapping: {
                        success: createAlertIcon(),
                        info: createAlertIcon(),
                        warning: createAlertIcon(),
                        error: createAlertIcon(),
                    },
                },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        fontWeight: 500,
                        alignItems: 'center',
                        padding: '12px 16px',
                        variants: [
                            {
                                props: { variant: 'standard', color: 'success' },
                                style: alertStandardStyle(alertColors.success, tintFadeBase, isDark),
                            },
                            {
                                props: { variant: 'standard', color: 'info' },
                                style: alertStandardStyle(alertColors.info, tintFadeBase, isDark),
                            },
                            {
                                props: { variant: 'standard', color: 'warning' },
                                style: alertStandardStyle(alertColors.warning, tintFadeBase, isDark),
                            },
                            {
                                props: { variant: 'standard', color: 'error' },
                                style: alertStandardStyle(alertColors.error, tintFadeBase, isDark),
                            },
                            {
                                props: { variant: 'standard', color: 'neutral' },
                                style: {
                                    ...alertStandardStyle(alertColors.neutral, tintFadeBase, isDark),
                                    color: isDark ? '#f4f4f5' : '#101828',
                                    '& .MuiAlert-icon': {
                                        color: isDark ? '#f4f4f5' : '#101828',
                                        opacity: 1,
                                    },
                                },
                            },
                            {
                                props: { variant: 'filled', color: 'success' },
                                style: alertFilledStyle(alertColors.success),
                            },
                            {
                                props: { variant: 'filled', color: 'info' },
                                style: alertFilledStyle(alertColors.info),
                            },
                            {
                                props: { variant: 'filled', color: 'warning' },
                                style: alertFilledStyle(alertColors.warning),
                            },
                            {
                                props: { variant: 'filled', color: 'error' },
                                style: alertFilledStyle(alertColors.error),
                            },
                            {
                                props: { variant: 'filled', color: 'neutral' },
                                style: alertFilledStyle(isDark ? '#f4f4f5' : '#ffffff', isDark ? '#16161a' : '#000000'),
                            },
                        ],
                    },
                    icon: {
                        opacity: 1,
                        alignSelf: 'center',
                        padding: 0,
                        marginRight: 12,
                        marginTop: 0,
                        fontSize: 20,
                        '& > svg': {
                            fontSize: 20,
                            width: 20,
                            height: 20,
                        },
                    },
                    message: {
                        padding: 0,
                        minWidth: 0,
                    },
                    action: {
                        alignItems: 'center',
                        paddingTop: 0,
                        marginRight: -4,
                        marginTop: 0,
                    },
                },
            },
            MuiAlertTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: 1.4,
                        marginTop: 0,
                        marginBottom: 4,
                        color: 'inherit',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:last-child td': {
                            borderBottom: 0,
                        },
                    },
                },
            },

            // Compact Switch has no padding; reset FormControlLabel's -11px margin and add gap.
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        marginLeft: 0,
                        gap: '8px',
                        '&:has(.MuiSwitch-root)': {
                            alignItems: 'flex-start',
                        },
                        '& .MuiSwitch-root': {
                            marginTop: '2px',
                        },
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        variants: [
                            {
                                // Dense variant for cards and cell actions — a 16px box that sits with 16px action icons.
                                props: { size: 'x-small' },
                                style: {
                                    padding: 4,
                                    '& .MuiSvgIcon-root': {
                                        fontSize: 16,
                                    },
                                },
                            },
                        ],
                    },
                },
            },
            MuiSwitch: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        width: 36,
                        height: 20,
                        padding: 0,
                        display: 'flex',
                        overflow: 'visible',
                        variants: [
                            {
                                props: { size: 'small' },
                                style: {
                                    width: 28,
                                    height: 16,
                                    '& .MuiSwitch-switchBase': {
                                        padding: 2,
                                        '&.Mui-checked': {
                                            transform: 'translateX(12px)',
                                        },
                                    },
                                    '& .MuiSwitch-thumb': {
                                        width: 12,
                                        height: 12,
                                    },
                                },
                            },
                            {
                                props: { color: 'sanya-primary' },
                                style: {
                                    '& .MuiSwitch-switchBase': {
                                        '&.Mui-checked': {
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-sanya-primary-bg)',
                                            },
                                            '&:hover + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-sanya-primary-bg-hover)',
                                            },
                                            '&.Mui-disabled + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-sanya-primary-bg-disabled)',
                                            },
                                        },
                                        '&.Mui-focusVisible + .MuiSwitch-track': {
                                            outline: '2px solid var(--btn-sanya-primary-bg)',
                                            outlineOffset: '2px',
                                        },
                                    },
                                },
                            },
                            {
                                props: { color: 'brand' },
                                style: {
                                    '& .MuiSwitch-switchBase': {
                                        '&.Mui-checked': {
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-brand-bg)',
                                            },
                                            '&:hover + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-brand-bg-hover)',
                                            },
                                            '&.Mui-disabled + .MuiSwitch-track': {
                                                backgroundColor: 'var(--btn-brand-bg-disabled)',
                                            },
                                        },
                                        '&.Mui-focusVisible + .MuiSwitch-track': {
                                            outline: '2px solid var(--btn-brand-bg)',
                                            outlineOffset: '2px',
                                        },
                                    },
                                },
                            },
                        ],
                    },
                    switchBase: {
                        padding: 2,
                        color: '#ffffff',
                        transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#ffffff',
                            '& + .MuiSwitch-track': {
                                backgroundColor: 'var(--primary)',
                                opacity: 1,
                            },
                            '&:hover + .MuiSwitch-track': {
                                backgroundColor: 'var(--primary)',
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                                backgroundColor: 'color-mix(in srgb, var(--primary) 35%, white)',
                                opacity: 1,
                            },
                        },
                        '&:hover:not(.Mui-checked):not(.Mui-disabled) + .MuiSwitch-track': {
                            backgroundColor: '#98a2b3',
                        },
                        '&.Mui-focusVisible + .MuiSwitch-track': {
                            outline: '2px solid var(--primary)',
                            outlineOffset: '2px',
                        },
                        '&.Mui-disabled': {
                            color: '#ffffff',
                            '& + .MuiSwitch-track': {
                                backgroundColor: '#eaecf0',
                                opacity: 1,
                            },
                        },
                    },
                    thumb: {
                        width: 16,
                        height: 16,
                        boxShadow: 'none',
                        backgroundColor: '#ffffff',
                    },
                    track: {
                        borderRadius: 999,
                        opacity: 1,
                        backgroundColor: '#d0d5dd',
                        boxSizing: 'border-box',
                    },
                },
            },
        },
    })
}
